import { useCustomMutation, useNotification } from '@refinedev/core';
import { Modal, Select } from 'antd';
import { Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { useState, type FC } from 'react';

interface Props {
  open: boolean;
  orgId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const PROVIDERS = [
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai', label: 'OpenAI (GPT)' },
];

const EnableByokModal: FC<Props> = ({ open, orgId, onClose, onSuccess }) => {
  const [provider, setProvider] = useState('anthropic');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const { open: notify } = useNotification();
  const { mutate: enableByok, isLoading } = useCustomMutation();
  const valid = apiKey.length >= 10;

  const handleSubmit = () => {
    if (!valid) return;
    enableByok(
      { url: `/api/v1/admin/subscription/organizations/${orgId}/byok/enable`, method: 'post', values: { apiKey, provider } },
      {
        onSuccess: () => {
          notify?.({ type: 'success', message: 'BYOK activé — clé chiffrée avec succès.' });
          setApiKey(''); setShowKey(false); onSuccess();
        },
        onError: () => notify?.({ type: 'error', message: "Erreur lors de l'activation BYOK." }),
      },
    );
  };

  const handleClose = () => { setApiKey(''); setShowKey(false); onClose(); };

  return (
    <Modal open={open} title="Activer BYOK" onCancel={handleClose} onOk={handleSubmit}
      okText="Activer BYOK" okButtonProps={{ loading: isLoading, disabled: !valid }} cancelText="Annuler" destroyOnClose>
      <div className="space-y-4 py-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Provider</label>
          <Select value={provider} onChange={setProvider} className="w-full" options={PROVIDERS} />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Clé API</label>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-ant-api03-..."
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pr-10 font-mono text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
            />
            <button type="button" onClick={() => setShowKey((v) => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            La clé sera chiffrée (Sodium) et ne sera plus visible après validation.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default EnableByokModal;
